import React, { useState } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import "../../utils/axiosAuth"; // installs the Authorization header interceptor
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API } from "../../apiConfig";
import { FaDatabase, FaFlask, FaRoute } from "react-icons/fa";

const UPDATE_ACTIONS = [
  {
    key: "tba-gtp",
    title: "Update TBA-GTP Table",
    description: "Refresh GTP standard values sourced from the TBA-GTP master table.",
    icon: FaDatabase,
  },
  {
    key: "qafg",
    title: "Update GTP QA-FG Table",
    description: "Refresh GTP standard values sourced from the QA-FG master table.",
    icon: FaFlask,
  },
  {
    key: "routing",
    title: "Update GTP Routing Table",
    description: "Refresh GTP standard values sourced from the Routing master table.",
    icon: FaRoute,
  },
];

function GTPDataUpdate() {
  const [activeAction, setActiveAction] = useState(null); // which modal is open
  const [uploading, setUploading] = useState(false);

  const openModal = (action) => setActiveAction(action);
  const closeModal = () => setActiveAction(null);

  const handleDownloadTemplate = async (action) => {
    try {
      // Must go through axios (not window.location) so the Authorization header is sent.
      const res = await axios.get(
        `${MASTER_API}/gtp-data-update/template/${action.key}`,
        { responseType: "blob" }
      );

      // Prefer the filename supplied by the server
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = match ? match[1] : `${action.key}_Template.xlsx`;

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // With responseType "blob", error bodies arrive as a Blob — read the JSON message out of it
      let message = "Template download failed";
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          message = JSON.parse(text)?.message || message;
        } else if (err.response?.data?.message) {
          message = err.response.data.message;
        }
      } catch {}
      toast.error(message);
    }
  };

  const handleFileUpload = async (e, action) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const res = await axios.post(
        `${MASTER_API}/gtp-data-update/update/${action.key}`,
        formData
      );
      toast.success(res.data?.message || "Updated successfully");
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  return (
    <Layout>
      <div style={styles.pageTitleBar}>
        <div>
          <div style={styles.pageTitle}>GTP Data Update</div>
          <div style={styles.pageSubtitle}>Trigger updates for the GTP-related master tables</div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <div style={styles.cardGrid}>
          {UPDATE_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <div key={action.key} style={styles.actionCard}>
                <div style={styles.actionIconWrap}>
                  <Icon size={20} color="#021843" />
                </div>
                <div style={styles.actionTitle}>{action.title}</div>
                <div style={styles.actionDescription}>{action.description}</div>
                <button style={{ ...styles.saveBtn, marginTop: "auto" }} onClick={() => openModal(action)}>
                  Update
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===================== UPLOAD MODAL (per action) ===================== */}
      {activeAction && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h4 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>{activeAction.title}</h4>
              <button onClick={closeModal} style={styles.closeBtn}>✖</button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <button style={styles.downloadBtn} onClick={() => handleDownloadTemplate(activeAction)}>
                  Download Template
                </button>
              </div>

              <div style={{ textAlign: "center" }}>
                <input
                  type="file"
                  id="gtpDataUploadInput"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileUpload(e, activeAction)}
                  disabled={uploading}
                />
                <label htmlFor="gtpDataUploadInput" style={styles.browseBtn}>
                  {uploading ? "Uploading..." : "Browse File"}
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick pauseOnHover />
    </Layout>
  );
}

/* ================= STYLES ================= */
const styles = {
  pageTitleBar:   { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 },
  pageTitle:      { fontSize: 30, fontWeight: 700, color: "#1e293b", marginBottom: 2 },
  pageSubtitle:   { fontSize: 12, color: "#64748b" },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "18px", padding: "12px" },
  actionCard: {
    background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px",
    boxShadow: "0 2px 8px rgba(15,23,42,0.06)", display: "flex", flexDirection: "column", minHeight: "180px",
  },
  actionIconWrap: {
    width: 40, height: 40, borderRadius: "8px", background: "#eef2ff",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "12px",
  },
  actionTitle:       { fontSize: "15px", fontWeight: 700, color: "#1e293b", marginBottom: "6px" },
  actionDescription: { fontSize: "12px", color: "#64748b", marginBottom: "16px", lineHeight: 1.5 },
  saveBtn: {
    background: "#021843", color: "#fff", border: "none", borderRadius: "8px",
    padding: "10px 26px", fontSize: "14px", fontWeight: 600, cursor: "pointer", alignSelf: "flex-start",
  },
  modalBackdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1060,
    display: "flex", justifyContent: "center", alignItems: "center",
  },
  modal: { background: "#fff", width: "450px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  closeBtn: { background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b" },
  modalBody: { background: "#f8fafc", border: "2px dashed #cbd5e1", borderRadius: 8, padding: 24 },
  downloadBtn: { background: "#021843", color: "#fff", padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  browseBtn: { background: "#021843", color: "#fff", padding: "10px 24px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block" },
};

export default GTPDataUpdate;